import type { Moment } from 'moment';
import moment from 'moment';

export type ShinkansenTicketPickupNotice = {
  title: string;
  descriptionLines: string[];
  noteLine: string;
  bulletLines: string[];
  stationLabel: string;
  stationLabelUrl?: string;
  withNotePrefix: boolean;
  phoneNumber?: string;
  manualUrl: string;
  manualLabel: string;
};

const AITRAVEL_SERVICE_ID = 1;
const TABIKOBO_SERVICE_ID = 2;
const AITRAVEL_QR_WEST_PHONE = '0344055753';
const TABIKOBO_QR_WEST_PHONE = '0359601672';
const JR_EAST_AREA_URL = 'https://www.eki-net.com/top/jrticket/guide/uketori/';
const JR_WEST_AREA_URL =
  'https://www.jr-odekake.net/goyoyaku/e5489/reservation/?_gl=1*cebitu*_ga*MTI5NTA1Njg5Mi4xNzA5Mjg3MTQ5*_ga_1EX2KJB2NH*MTcxMDQyNzY1Mi40LjEuMTcxMDQyNzg0Ni42MC4wLjA';
// 新マニュアル切替の受付カットオフ（2026-08-14 09:00 JST）。BE側 Domain::Calendar::ShinkansenTicketPickupNotice と同一契約
const NEW_MANUAL_START_AT = '2026-08-14T09:00:00+09:00';
const MANUAL_URLS: { [key: string]: { old: string; new: string } } = {
  qr_east: {
    old: 'https://aitravel.my.salesforce.com/sfc/p/#7F000002CrgE/a/TL000009oCSD/60BPpg_FMjI78FC9EbT7_gTD17p8dxYsLytkC6I9gug',
    new: 'https://aitravel.my.salesforce.com/sfc/p/7F000002CrgE/a/TL00000CwIX7/H1nxZX_bod3vW5CTkKLk_als_gYfFuqzE9ARAcJyGxo'
  },
  qr_west: {
    old: 'https://aitravel.my.salesforce.com/sfc/p/#7F000002CrgE/a/TL000009oClZ/WjqKmA7UVdktcrsHXT8knfBL5_EIgQ0Ie8aNma_Epvw',
    new: 'https://aitravel.my.salesforce.com/sfc/p/7F000002CrgE/a/TL00000CwIaL/qKncf7zm0ZC4XZeAfvF8NNC4VdKTbgIEh5vJOghEMGk'
  }
};

const resolveManualUrl = (
  ticketingMethod: string,
  serviceId?: number | null,
  orderItemCreatedAt?: Moment | null
): string => {
  const urls = MANUAL_URLS[ticketingMethod];
  // 旅工房（OEM）・受付日時不明は旧URL維持の安全側フォールバック
  const newManualTarget =
    serviceId === AITRAVEL_SERVICE_ID &&
    orderItemCreatedAt != null &&
    orderItemCreatedAt.isSameOrAfter(moment(NEW_MANUAL_START_AT));
  return newManualTarget ? urls.new : urls.old;
};

export const resolveQrWestPhoneNumber = (serviceId?: number | null): string => {
  if (serviceId === TABIKOBO_SERVICE_ID) {
    return TABIKOBO_QR_WEST_PHONE;
  }
  return AITRAVEL_QR_WEST_PHONE;
};

export const shouldShowShinkansenTicketPickupNotice = (serviceId?: number | null): boolean => {
  return serviceId === AITRAVEL_SERVICE_ID || serviceId === TABIKOBO_SERVICE_ID;
};

const isQrTicketingMethod = (ticketingMethod?: string | null): boolean =>
  ticketingMethod === 'qr_east' || ticketingMethod === 'qr_west';

const isAreaBasedPickup = (pickupStation?: string | null): boolean => !pickupStation || pickupStation === 'その他';

const resolvePickupStationLabel = (pickupStation?: string | null, ticketingMethod?: string | null): string => {
  if (pickupStation && pickupStation !== 'その他') {
    return pickupStation;
  }
  if (ticketingMethod === 'qr_east') {
    return 'JR東日本エリアの駅';
  }
  if (ticketingMethod === 'qr_west') {
    return 'JR西日本エリアの駅';
  }
  return '選択した発券サービスの対応駅';
};

const resolveStationLabelUrl = (
  pickupStation?: string | null,
  ticketingMethod?: string | null
): string | undefined => {
  if (!isAreaBasedPickup(pickupStation)) {
    return undefined;
  }
  if (ticketingMethod === 'qr_east') {
    return JR_EAST_AREA_URL;
  }
  if (ticketingMethod === 'qr_west') {
    return JR_WEST_AREA_URL;
  }
  return undefined;
};

const buildBulletLines = (stationLabel: string): string[] => {
  return [
    `・${stationLabel}でチケットをお受け取りください`,
    '・選択した受け取り場所以外ではチケットを受け取れない場合があります',
    '・上記に沿わない場合、ご予約いただいた内容でご乗車いただくことができなくなります'
  ];
};

const buildNoteLine = (stationLabel: string, withNotePrefix: boolean): string => {
  const body = `チケットは「${stationLabel}」で受け取る必要があります`;
  return withNotePrefix ? `※${body}` : body;
};

export const buildShinkansenCalendarTitlePrefix = (
  ticketingMethod?: string | null,
  pickupStation?: string | null
): string | null => {
  if (!isQrTicketingMethod(ticketingMethod) && !pickupStation) {
    return null;
  }
  const stationLabel = resolvePickupStationLabel(pickupStation, ticketingMethod);
  return `【${stationLabel}で受取】`;
};

export const buildShinkansenTicketPickupNotice = (
  ticketingMethod?: string | null,
  pickupStation?: string | null,
  serviceId?: number | null,
  orderItemCreatedAt?: Moment | null
): ShinkansenTicketPickupNotice | null => {
  const stationLabel = resolvePickupStationLabel(pickupStation, ticketingMethod);
  const stationLabelUrl = resolveStationLabelUrl(pickupStation, ticketingMethod);
  const bulletLines = buildBulletLines(stationLabel);

  switch (ticketingMethod) {
    case 'qr_west':
      return {
        title: '【新幹線チケット受け取りについて】',
        descriptionLines: [
          'e5489でご予約の新幹線チケットは、',
          '発券機でQRコードを読み取り後、電話番号の入力を求められます。'
        ],
        noteLine: buildNoteLine(stationLabel, true),
        bulletLines,
        stationLabel,
        stationLabelUrl,
        withNotePrefix: true,
        phoneNumber: resolveQrWestPhoneNumber(serviceId),
        manualUrl: resolveManualUrl('qr_west', serviceId, orderItemCreatedAt),
        manualLabel: 'e5489チケットご利用マニュアル'
      };
    case 'qr_east':
      return {
        title: '【新幹線チケット受け取りについて】',
        descriptionLines: [],
        noteLine: buildNoteLine(stationLabel, false),
        bulletLines,
        stationLabel,
        stationLabelUrl,
        withNotePrefix: false,
        manualUrl: resolveManualUrl('qr_east', serviceId, orderItemCreatedAt),
        manualLabel: 'ビジネスえきねっとチケットご利用マニュアル'
      };
    default:
      return null;
  }
};

export const formatPickupNoticeNoteLine = (
  notice: ShinkansenTicketPickupNotice,
  calendarType: 'google' | 'outlook',
  addLink: (url: string, text?: string) => string
): string => {
  if (!notice.stationLabelUrl) {
    return notice.noteLine;
  }
  const quotedLabel = `「${notice.stationLabel}」`;
  if (calendarType === 'google') {
    return notice.noteLine.replace(quotedLabel, `「${notice.stationLabel} (${notice.stationLabelUrl})」`);
  }
  return notice.noteLine.replace(quotedLabel, `「${addLink(notice.stationLabelUrl, notice.stationLabel)}」`);
};

export const formatPickupNoticeBulletLine = (
  line: string,
  notice: ShinkansenTicketPickupNotice,
  calendarType: 'google' | 'outlook',
  addLink: (url: string, text?: string) => string
): string => {
  const stationPrefix = `・${notice.stationLabel}`;
  if (!line.startsWith(stationPrefix) || !notice.stationLabelUrl) {
    return line;
  }
  const suffix = line.slice(stationPrefix.length);
  if (calendarType === 'google') {
    return `・${notice.stationLabel} (${notice.stationLabelUrl})${suffix}`;
  }
  return `・${addLink(notice.stationLabelUrl, notice.stationLabel)}${suffix}`;
};
